import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { serviceAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Scissors,
  Clock,
  Euro,
  Search,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';

// Form component outside main component
function ServiceForm({ formData, setFormData, serviceCategories }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Nome Servizio *</Label>
        <Input
          placeholder="es. Taglio Uomo"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label>Descrizione</Label>
        <Textarea
          placeholder="Descrizione del servizio..."
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={2}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Prezzo (€) *</Label>
          <div className="relative">
            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="15.00"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              className="pl-10"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Durata (minuti)</Label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="number"
              min="5"
              step="5"
              placeholder="30"
              value={formData.duration_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
              className="pl-10"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Categoria</Label>
        <Select
          value={formData.category_id || "none"}
          onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v === "none" ? "" : v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Nessuna categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nessuna categoria</SelectItem>
            {serviceCategories.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div>
          <p className="font-medium">Servizio Attivo</p>
          <p className="text-sm text-muted-foreground">I servizi disattivati non saranno visibili ai clienti</p>
        </div>
        <Switch
          checked={formData.is_active}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
        />
      </div>
    </div>
  );
}

export default function ServiceManager({ services, categories, onRefresh }) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_minutes: '30',
    category_id: '',
    is_active: true
  });

  const serviceCategories = categories.filter(c => c.type === 'service');

  // Filter services
  const filteredServices = useMemo(() => {
    return services.filter(service => {
      if (searchTerm && !service.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (categoryFilter !== 'all' && service.category_id !== categoryFilter) {
        return false;
      }
      return true;
    });
  }, [services, searchTerm, categoryFilter]);

  // Paginate
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);
  const paginatedServices = useMemo(() => {
    return filteredServices.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
  }, [filteredServices, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter]);

  const hasActiveFilters = searchTerm || categoryFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setCurrentPage(1);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      duration_minutes: '30',
      category_id: '',
      is_active: true
    });
  };

  const openCreate = () => {
    resetForm();
    setIsCreateOpen(true);
  };

  const openEdit = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name || '',
      description: service.description || '',
      price: service.price?.toString() || '',
      duration_minutes: service.duration_minutes?.toString() || '30',
      category_id: service.category_id || '',
      is_active: service.is_active !== false
    });
    setIsEditOpen(true);
  };

  const openDelete = (service) => {
    setSelectedService(service);
    setIsDeleteOpen(true);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.price) {
      toast.error('Nome e prezzo sono obbligatori');
      return;
    }

    setLoading(true);
    try {
      await serviceAPI.create({
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        duration_minutes: parseInt(formData.duration_minutes) || 30,
        category_id: formData.category_id || null,
        is_active: formData.is_active
      });
      toast.success('Servizio creato con successo');
      setIsCreateOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedService) return;

    setLoading(true);
    try {
      await serviceAPI.update(selectedService.id, {
        name: formData.name,
        description: formData.description || null,
        price: parseFloat(formData.price),
        duration_minutes: parseInt(formData.duration_minutes) || 30,
        category_id: formData.category_id || null,
        is_active: formData.is_active
      });
      toast.success('Servizio aggiornato');
      setIsEditOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;

    setLoading(true);
    try {
      await serviceAPI.delete(selectedService.id);
      toast.success('Servizio eliminato');
      setIsDeleteOpen(false);
      setSelectedService(null);
      onRefresh();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || '-';
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Gestione Servizi</CardTitle>
        <Button onClick={openCreate} data-testid="create-service-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Servizio
        </Button>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca servizio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="service-search-input"
              />
            </div>
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="service-category-filter">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {serviceCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-service-filters">
              <X className="w-4 h-4 mr-1" />
              Cancella filtri
            </Button>
          )}

          <Badge variant="secondary" className="h-10 px-3 flex items-center">
            {filteredServices.length} risultati
          </Badge>
        </div>

        {filteredServices.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">
            {hasActiveFilters ? 'Nessun servizio trovato con i filtri selezionati' : 'Nessun servizio'}
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedServices.map(service => (
              <Card key={service.id} className={`overflow-hidden ${!service.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Scissors className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{service.name}</p>
                        {service.category_id && (
                          <Badge variant="outline" className="text-xs">{getCategoryName(service.category_id)}</Badge>
                        )}
                      </div>
                    </div>
                    {!service.is_active && (
                      <Badge variant="secondary" className="text-xs">Disattivo</Badge>
                    )}
                  </div>
                  
                  {service.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{service.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex gap-3 text-sm">
                      <span className="font-semibold text-primary">€{service.price?.toFixed(2)}</span>
                      <span className="text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {service.duration_minutes} min
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(service)}
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDelete(service)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              data-testid="service-prev-page"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={pageNum === currentPage ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCurrentPage(pageNum)}
                  data-testid={`service-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              );
            })}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              data-testid="service-next-page"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5" />
              Nuovo Servizio
            </DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo servizio al catalogo
            </DialogDescription>
          </DialogHeader>
          <ServiceForm formData={formData} setFormData={setFormData} serviceCategories={serviceCategories} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creazione...' : 'Crea Servizio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifica Servizio
            </DialogTitle>
          </DialogHeader>
          <ServiceForm formData={formData} setFormData={setFormData} serviceCategories={serviceCategories} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={loading}>
              {loading ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription asChild>
              <div>
                Sei sicuro di voler eliminare questo servizio?
                {selectedService && (
                  <div className="mt-2 p-3 bg-muted rounded-md">
                    <p><strong>Nome:</strong> {selectedService.name}</p>
                    <p><strong>Prezzo:</strong> €{selectedService.price?.toFixed(2)}</p>
                  </div>
                )}
                <p className="mt-2 text-amber-600 text-sm">
                  Gli appuntamenti esistenti con questo servizio non saranno cancellati.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
